import { DateTime } from 'luxon';

/** אזור הזמן של המבצע כברירת מחדל. המנהל משנה אותו בהגדרות כשהמבצע מתקיים במקום אחר (למשל אירופה) */
export const DEFAULT_GAME_TZ = 'Asia/Jerusalem';
/** @deprecated השתמשו ב-DEFAULT_GAME_TZ, או באזור הזמן מההגדרות (settings.timeZone) */
export const GAME_TZ = DEFAULT_GAME_TZ;

/** האם זה מזהה אזור זמן תקין (IANA, למשל Europe/Paris) */
export function isValidTimeZone(zone: string | null | undefined): zone is string {
  if (!zone || zone.length > 64) return false;
  return DateTime.now().setZone(zone).isValid;
}

/** ההפרש מ-UTC בדקות באזור הזמן, ברגע נתון (כולל שעון קיץ) */
export function offsetMinutes(zone: string, at: Date = new Date()): number {
  const dt = DateTime.fromMillis(at.getTime(), { zone });
  return dt.isValid ? dt.offset : 0;
}

const DATE_RE = /^(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})$/;
const TIME_RE = /^(\d{1,2}):(\d{2})(?::\d{2})?$/;

export interface ParsedDate {
  day: number;
  month: number;
  year: number;
}

export interface ParsedTime {
  hour: number;
  minute: number;
}

/** מפרש תאריך בפורמט הגיליון: 25/05/26 או 25/05/2026 (גם עם נקודות/מקפים) */
export function parseGameDate(raw: string | null | undefined): ParsedDate | null {
  if (!raw) return null;
  const m = DATE_RE.exec(raw.trim());
  if (!m) return null;
  const day = Number(m[1]);
  const month = Number(m[2]);
  let year = Number(m[3]);
  if (year < 100) year += 2000;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { day, month, year };
}

/** מפרש שעה בפורמט הגיליון: 17:30 או 17:30:00 */
export function parseGameTime(raw: string | null | undefined): ParsedTime | null {
  if (!raw) return null;
  const m = TIME_RE.exec(raw.trim());
  if (!m) return null;
  const hour = Number(m[1]);
  const minute = Number(m[2]);
  if (hour > 23 || minute > 59) return null;
  return { hour, minute };
}

/**
 * מחשב את רגע האפשור: תאריך + שעה מקומיים באזור הזמן של המבצע (ברירת מחדל Asia/Jerusalem) → ISO ב-UTC.
 * חובה ששני הרכיבים יהיו תקינים — שעה בלי תאריך אינה תנאי זמן תקף.
 */
export function unlockInstantIso(
  dateRaw: string | null | undefined,
  timeRaw: string | null | undefined,
  zone: string = DEFAULT_GAME_TZ,
): string | null {
  const d = parseGameDate(dateRaw);
  const t = parseGameTime(timeRaw);
  if (!d || !t) return null;
  const dt = DateTime.fromObject(
    { year: d.year, month: d.month, day: d.day, hour: t.hour, minute: t.minute },
    { zone },
  );
  return dt.isValid ? dt.toUTC().toISO() : null;
}

/** שמות הימים כפי שהם נכתבים בגיליון, לפי אינדקס Luxon weekday % 7 (0=ראשון..6=שבת) */
export const HEBREW_DAY_NAMES = [
  'יום ראשון',
  'יום שני',
  'יום שלישי',
  'יום רביעי',
  'יום חמישי',
  'יום שישי',
  'יום שבת',
] as const;

/**
 * שם היום בעברית מתוך תאריך בפורמט הגיליון — כדי שהמנהל לא יצטרך למלא גם יום
 * וגם תאריך בעריכת משימה (היום נגזר אוטומטית מהתאריך). מחזיר null אם התאריך
 * לא תקין/חסר.
 */
export function hebrewDayName(dateRaw: string | null | undefined): string | null {
  const d = parseGameDate(dateRaw);
  if (!d) return null;
  // היום בשבוע של תאריך לוח שנה אינו תלוי באזור הזמן
  const dt = DateTime.fromObject({ year: d.year, month: d.month, day: d.day }, { zone: 'utc' });
  if (!dt.isValid) return null;
  return HEBREW_DAY_NAMES[dt.weekday % 7]!;
}

/** תצוגת תאריך מקומית DD/MM/YYYY באזור הזמן של המבצע */
export function formatHeDate(iso: string, zone: string = DEFAULT_GAME_TZ): string {
  const dt = DateTime.fromISO(iso, { zone: 'utc' }).setZone(zone);
  return dt.isValid ? dt.toFormat('dd/MM/yyyy') : iso;
}

/** תצוגת שעה מקומית HH:mm באזור הזמן של המבצע */
export function formatHeTime(iso: string, zone: string = DEFAULT_GAME_TZ): string {
  const dt = DateTime.fromISO(iso, { zone: 'utc' }).setZone(zone);
  return dt.isValid ? dt.toFormat('HH:mm') : iso;
}

export function nowIso(): string {
  return new Date().toISOString();
}
